/**
 * RecipesPage - Beredskapsrecept
 */

import { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { useRecipesStore, type Recipe, type RecipeIngredient, type RecipeInstruction } from '@/stores/recipes-store';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuthStore } from '@/stores/auth-store';
import { useFamilyStore, useFamilyHydrated } from '@/stores/family-store';
import { useInventoryStore, type InventoryCategory, CATEGORY_LABELS } from '@/stores/inventory-store';
import { useFavoriteStore } from '@/stores/favorite-store';
import { useShoppingListStore } from '@/stores/shopping-list-store';
import { useMealPlanStore, type MealPlanItem } from '@/stores/meal-plan-store';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import PremiumRequiredModal from '@/components/PremiumRequiredModal';
import ModalBackdrop from '@/components/ModalBackdrop';
import {
  Plus,
  Search,
  X,
  Trash2,
  Edit2,
  ChefHat,
  Pin,
  LayoutGrid,
  Rows3,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Minus as MinusCircle,
  Plus as PlusCircle,
  Package,
  ShoppingCart,
  Check,
  Minus,
  UtensilsCrossed,
  Copy,
  Flag,
  CalendarOff,
  GripVertical,
} from 'lucide-react';

const COMMON_AISLES = [
  'Frukt & Grönt',
  'Mejeri',
  'Kött & Chark',
  'Fisk & Skaldjur',
  'Bröd & Bageri',
  'Konserver',
  'Torrvaror',
  'Fryst',
  'Drycker',
  'Godis & Snacks',
  'Kemi & Städning',
  'Hygien',
  'Djurmat',
  'Hushåll',
  'Baby',
];

const COMMON_UNITS = [
  'st',
  'g',
  'kg',
  'ml',
  'dl',
  'l',
  'tsk',
  'msk',
  'krm',
  'burk',
  'påse',
  'förp',
];

interface SortableMealItemProps {
  item: MealPlanItem;
  recipeNumber: number;
  recipes: Recipe[];
  onViewRecipe: (recipe: Recipe) => void;
  onRemove: (id: string) => void;
}

function SortableMealItem({ item, recipeNumber, recipes, onViewRecipe, onRemove }: SortableMealItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const isMarker = item.type === 'marker';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`flex items-center gap-3 rounded-lg border transition-colors ${
        isMarker 
          ? 'p-2 border-border/50 bg-muted/10 hover:bg-muted/20' 
          : 'p-4 border-border bg-muted/30 hover:bg-muted/50'
      }`}
    >
      {/* Drag Handle Icon */}
      <div
        ref={setActivatorNodeRef}
        {...listeners}
        className={`flex-shrink-0 cursor-grab active:cursor-grabbing touch-none`}
        title="Dra för att flytta"
      >
        <GripVertical className={`${isMarker ? 'h-4 w-4' : 'h-5 w-5'} text-muted-foreground`} />
      </div>

      {isMarker ? (
        // Marker view
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <Flag className="h-3.5 w-3.5 flex-shrink-0" style={{ color: '#ba875d', opacity: 0.6 }} />
          <span className="text-sm italic soft-wrap" style={{ color: '#ba875d', opacity: 0.8 }}>
            {item.markerLabel}
          </span>
          {item.markerEndDate && (
            <span className="text-xs flex items-center gap-1" style={{ color: '#ba875d', opacity: 0.6 }}>
              (<CalendarOff className="h-3 w-3" />{new Date(item.markerEndDate).toLocaleDateString('sv-SE')})
            </span>
          )}
        </div>
      ) : (
        // Recipe view
        <button
          onClick={() => {
            const recipe = recipes.find(r => r.id === item.recipeId);
            if (recipe) {
              onViewRecipe(recipe);
            }
          }}
          onPointerDown={(e) => e.stopPropagation()}
          className="flex-1 min-w-0 text-left hover:opacity-80 transition-opacity cursor-pointer"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">#{recipeNumber}</span>
            <h3 className="font-semibold truncate">{item.recipeName}</h3>
          </div>
        </button>
      )}

      <button
        onClick={() => onRemove(item.id)}
        onPointerDown={(e) => e.stopPropagation()}
        className="p-2 rounded hover:bg-background transition-colors cursor-pointer"
        title={isMarker ? 'Ta bort markör' : 'Ta bort från matsedel'}
      >
        <Trash2 className={`${isMarker ? 'h-3.5 w-3.5' : 'h-4 w-4'} text-red-400`} />
      </button>
    </div>
  );
}

export default function RecipesPage() {
  const { isAuthenticated, user } = useAuthStore();
  const { family } = useFamilyStore();
  const familyHydrated = useFamilyHydrated();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract stable values for useEffect dependencies
  const userFamilyId = user?.familyId;
  const familyId = family?.id;
  
  // Start with loading state to prevent flash of wrong content
  const [isLoadingRecipes, setIsLoadingRecipes] = useState(false);
  const [isRefreshingRecipes, setIsRefreshingRecipes] = useState(false);
  const [recipeLoadError, setRecipeLoadError] = useState('');
  const [recipeReloadToken, setRecipeReloadToken] = useState(0);
  const lastHandledRecipeReloadToken = useRef(0);
  
  const {
    recipes,
    totalCount,
    loadedScopeKey,
    fetchFromServer,
    clearRecipes,
    addRecipe,
    updateRecipe,
    deleteRecipe,
    togglePin,
    searchRecipes,
  } = useRecipesStore();
  
  const { items: inventoryItems, fetchFromServer: fetchInventory } = useInventoryStore();
  const { favorites, fetchFromServer: fetchFavorites } = useFavoriteStore();
  const { 
    lists: shoppingLists, 
    items: shoppingItems,
    createItem: createShoppingItem,
    fetchFromServer: fetchShoppingLists 
  } = useShoppingListStore();
  
  const {
    items: mealPlanItems,
    addRecipe: addToMealPlan,
    addMarker: addMealPlanMarker,
    removeItem: removeFromMealPlan,
    reorderItems: reorderMealPlan,
    fetchFromServer: fetchMealPlan,
  } = useMealPlanStore();

  const recipesInMealPlan = useMemo(() => {
    const ids = new Set<string>();
    for (const item of mealPlanItems) {
      if (item.type === 'recipe' && item.recipeId) {
        ids.add(item.recipeId);
      }
    }
    return ids;
  }, [mealPlanItems]);
  
  // UI State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<Recipe | null>(null);
  const [shouldReturnToViewAfterEdit, setShouldReturnToViewAfterEdit] = useState(false);
  const [viewingRecipe, setViewingRecipe] = useState<Recipe | null>(null);
  const [viewingServings, setViewingServings] = useState<number>(1); // For adjusting servings in view mode
  const [checkedInstructionIds, setCheckedInstructionIds] = useState<Set<string>>(new Set());
  const [confirmDeleteRecipe, setConfirmDeleteRecipe] = useState<Recipe | null>(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumFeature, setPremiumFeature] = useState<string>('');
  const [addItemNotification, setAddItemNotification] = useState('');
  const [addMealPlanNotification, setAddMealPlanNotification] = useState('');
  const [copiedRecipe, setCopiedRecipe] = useState(false);
  const [showMealPlanModal, setShowMealPlanModal] = useState(false);
  const [showAddMarkerModal, setShowAddMarkerModal] = useState(false);
  const [showAddToShoppingModal, setShowAddToShoppingModal] = useState(false);
  const [markerForm, setMarkerForm] = useState({ label: '', endDate: '' });
  const [shoppingItemForm, setShoppingItemForm] = useState({
    listId: '',
    name: '',
    category: '' as InventoryCategory | '',
    notes: '',
    quantity: 1,
    store: '',
    aisle: '',
  });
  const [showStoreSuggestions, setShowStoreSuggestions] = useState(false);
  const [storeSuggestionSearch, setStoreSuggestionSearch] = useState('');
  const [showAisleSuggestions, setShowAisleSuggestions] = useState(false);
  const [aisleSuggestionSearch, setAisleSuggestionSearch] = useState('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [gridColumns, setGridColumns] = useState<'single' | 'gallery'>(() => {
    try {
      const stored = localStorage.getItem('recipesGridColumns');
      return (stored === 'single' || stored === 'gallery') ? stored : 'gallery';
    } catch {
      return 'gallery';
    }
  });
  
  // Form state
  const [formDish, setFormDish] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formServings, setFormServings] = useState(4);
  const [formIngredients, setFormIngredients] = useState<RecipeIngredient[]>([]);
  const [formInstructions, setFormInstructions] = useState<RecipeInstruction[]>([]);
  const [formTags, setFormTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [showTagPicker, setShowTagPicker] = useState(false);
  
  // Drag and drop sensors for meal plan
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required to start drag (better for touch)
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end for meal plan reordering
  const handleMealPlanDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = mealPlanItems.findIndex((item) => item.id === active.id);
      const newIndex = mealPlanItems.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedItems = arrayMove(mealPlanItems, oldIndex, newIndex);
        const itemsWithUpdatedPositions = reorderedItems.map((item, index) => ({
          ...item,
          position: index,
        }));

        try {
          await reorderMealPlan(itemsWithUpdatedPositions);
        } catch (error) {
          console.error('Failed to reorder meal plan:', error);
        }
      }
    }
  };
  
  // Premium access check
  const hasPremiumAccess = user?.subscriptionTier === 'premium' || !!family;
  const recipeScopeKey = isAuthenticated ? (familyId ? `family:${familyId}` : user?.id ? `user:${user.id}` : null) : null;

  const retryRecipeLoad = () => {
    setRecipeLoadError('');
    setRecipeReloadToken((current) => current + 1);
  };
  
  // Fetch recipes on mount and when family changes
  useEffect(() => {
    // Don't fetch until authenticated and family hydrated
    if (!familyHydrated || !isAuthenticated) {
      return;
    }
    
    // If user has a familyId but family hasn't loaded yet, wait!
    if (userFamilyId && !familyId) {
      return;
    }

    if (!recipeScopeKey) {
      return;
    }

    const isReloadRequest = recipeReloadToken !== lastHandledRecipeReloadToken.current;
    const hasCachedRecipesForScope = loadedScopeKey === recipeScopeKey;
    const needsInitialFetch = !hasCachedRecipesForScope;

    if (!needsInitialFetch && !isReloadRequest) {
      return;
    }

    let isActive = true;
    const loadTimeout = window.setTimeout(() => {
      if (!isActive) {
        return;
      }

      setIsLoadingRecipes(false);
      setIsRefreshingRecipes(false);
      setRecipeLoadError('Det tog ovanligt lång tid att ladda recepten. Du kan fortsätta använda sidan och prova igen.');
    }, 10000);

    lastHandledRecipeReloadToken.current = recipeReloadToken;
    setRecipeLoadError('');

    if (hasCachedRecipesForScope && recipes.length > 0) {
      setIsRefreshingRecipes(true);
    } else {
      if (loadedScopeKey && loadedScopeKey !== recipeScopeKey) {
        clearRecipes();
      }
      setIsLoadingRecipes(true);
    }

    fetchFromServer(recipeScopeKey).then((success) => {
      if (!isActive) {
        return;
      }

      if (!success) {
        setRecipeLoadError('Kunde inte ladda recepten just nu. Försök igen.');
      }
    }).finally(() => {
      window.clearTimeout(loadTimeout);

      if (!isActive) {
        return;
      }

      setIsLoadingRecipes(false);
      setIsRefreshingRecipes(false);
    });

    return () => {
      isActive = false;
      window.clearTimeout(loadTimeout);
    };
  }, [familyHydrated, isAuthenticated, familyId, userFamilyId, recipeScopeKey, loadedScopeKey, recipeReloadToken, fetchFromServer, clearRecipes, recipes.length]);
  
  // Fetch inventory items for integration
  useEffect(() => {
    if (!familyHydrated || !isAuthenticated) {
      return;
    }
    
    if (userFamilyId && !familyId) {
      return;
    }
    
    fetchInventory(familyId);
  }, [familyHydrated, isAuthenticated, familyId, userFamilyId, fetchInventory]);
  
  // Fetch favorites for shopping list integration
  useEffect(() => {
    if (!familyHydrated || !isAuthenticated) {
      return;
    }
    
    if (userFamilyId && !familyId) {
      return;
    }
    
    fetchFavorites(familyId);
  }, [familyHydrated, isAuthenticated, familyId, userFamilyId, fetchFavorites]);
  
  // Fetch shopping lists for quick add integration
  useEffect(() => {
    if (!familyHydrated || !isAuthenticated) {
      return;
    }
    
    if (userFamilyId && !familyId) {
      return;
    }
    
    fetchShoppingLists(familyId);
  }, [familyHydrated, isAuthenticated, familyId, userFamilyId, fetchShoppingLists]);
  
  // Fetch meal plan
  useEffect(() => {
    if (!familyHydrated || !isAuthenticated) {
      return;
    }
    
    if (userFamilyId && !familyId) {
      return;
    }
    
    fetchMealPlan(familyId);
  }, [familyHydrated, isAuthenticated, familyId, userFamilyId, fetchMealPlan]);
  
  // Save grid preference
  useEffect(() => {
    try {
      localStorage.setItem('recipesGridColumns', gridColumns);
    } catch {
      // Ignore storage errors
    }
  }, [gridColumns]);
  
  // Clean up expired markers when meal plan modal opens
  useEffect(() => {
    if (showMealPlanModal) {
      const now = new Date();
      now.setHours(0, 0, 0, 0); // Start of today
      
      mealPlanItems.forEach(item => {
        // Check if it's a marker with an end date
        if (item.markerLabel && item.markerEndDate) {
          const endDate = new Date(item.markerEndDate);
          endDate.setHours(0, 0, 0, 0);
          
          // Remove if end date has passed
          if (endDate < now) {
            removeFromMealPlan(item.id);
          }
        }
      });
    }
  }, [showMealPlanModal, mealPlanItems, removeFromMealPlan]);
  
  // Open meal plan modal if navigated from dashboard
  useLayoutEffect(() => {
    if (location.state && (location.state as any).openMealPlan) {
      // Open modal immediately to prevent scroll issues
      setShowMealPlanModal(true);
      // Clear the state to prevent modal from reopening on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);
  
  // Filtered and sorted recipes
  const filteredRecipes = useMemo(() => {
    let result = recipes;
    
    // Filter by search query
    if (searchQuery) {
      result = searchRecipes(searchQuery);
    }
    
    // Filter by tag
    if (selectedTag) {
      result = result.filter((r) => r.tags.includes(selectedTag));
    }
    
    // Sort: pinned first, then by date
    return [...result].sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [recipes, searchQuery, selectedTag, searchRecipes]);
  
  // All available tags
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    for (const recipe of recipes) {
      for (const tag of recipe.tags) {
        const trimmedTag = tag.trim();
        if (trimmedTag) tags.add(trimmedTag);
      }
    }
    return Array.from(tags).sort((a, b) => a.localeCompare(b, 'sv-SE'));
  }, [recipes]);
  
  // Hämta alla unika butiker från befintliga inköpslistor
  const existingStores = useMemo(() => {
    const stores = new Set<string>();
    shoppingItems.forEach(item => {
      if (item.store) stores.add(item.store);
    });
    return Array.from(stores).sort();
  }, [shoppingItems]);

  const filteredStores = useMemo(() => {
    if (!storeSuggestionSearch) return existingStores;
    return existingStores.filter(s => 
      s.toLowerCase().includes(storeSuggestionSearch.toLowerCase())
    );
  }, [existingStores, storeSuggestionSearch]);

  const filteredAisles = useMemo(() => {
    if (!aisleSuggestionSearch) return COMMON_AISLES;
    return COMMON_AISLES.filter(a => 
      a.toLowerCase().includes(aisleSuggestionSearch.toLowerCase())
    );
  }, [aisleSuggestionSearch]);
  
  // Open add modal
  const openAddModal = () => {
    if (!hasPremiumAccess) {
      setPremiumFeature('Receptbok');
      setShowPremiumModal(true);
      return;
    }
    resetForm();
    setShowAddModal(true);
  };
  
  // Open edit modal
  const openEditModal = (recipe: Recipe, options?: { returnToViewOnClose?: boolean }) => {
    if (!hasPremiumAccess) {
      setPremiumFeature('Receptbok');
      setShowPremiumModal(true);
      return;
    }
    setFormDish(recipe.dish);
    setFormDescription(recipe.description);
    setFormServings(recipe.servings);
    setFormIngredients([...recipe.ingredients]);
    setFormInstructions([...recipe.instructions]);
    setFormTags([...recipe.tags]);
    setShouldReturnToViewAfterEdit(!!options?.returnToViewOnClose);
    setEditingRecipe(recipe);
  };
  
  // Reset form
  const resetForm = () => {
    setFormDish('');
    setFormDescription('');
    setFormServings(4);
    setFormIngredients([]);
    setFormInstructions([]);
    setFormTags([]);
    setTagInput('');
  };
  
  // Close modals
  const closeAddModal = () => {
    setShowAddModal(false);
    resetForm();
  };
  
  const closeEditModal = () => {
    const recipeToRestore = shouldReturnToViewAfterEdit ? editingRecipe : null;
    setEditingRecipe(null);
    setShouldReturnToViewAfterEdit(false);
    resetForm();

    if (recipeToRestore) {
      openViewModal(recipeToRestore);
    }
  };

  const openViewModal = (recipe: Recipe) => {
    setViewingRecipe(recipe);
    setViewingServings(recipe.servings);
    setCheckedInstructionIds(new Set());
  };
  
  const copyRecipeToClipboard = async () => {
    if (!viewingRecipe) return;
    
    // Format recipe as text
    let recipeText = `${viewingRecipe.dish}\n`;
    recipeText += '='.repeat(viewingRecipe.dish.length) + '\n\n';
    
    if (viewingRecipe.description) {
      recipeText += `${viewingRecipe.description}\n\n`;
    }
    
    recipeText += `Portioner: ${viewingServings}\n\n`;
    
    recipeText += 'INGREDIENSER\n';
    recipeText += '-'.repeat(40) + '\n';
    viewingRecipe.ingredients
      .sort((a, b) => a.order - b.order)
      .forEach((ingredient) => {
        const amount = scaleIngredient(ingredient, viewingRecipe.servings, viewingServings);
        recipeText += `• ${amount} ${ingredient.unit} ${ingredient.item}\n`;
      });
    
    recipeText += '\n';
    recipeText += 'INSTRUKTIONER\n';
    recipeText += '-'.repeat(40) + '\n';
    viewingRecipe.instructions
      .sort((a, b) => a.step - b.step)
      .forEach((instruction) => {
        recipeText += `${instruction.step}. ${instruction.text}\n\n`;
      });
    
    try {
      await navigator.clipboard.writeText(recipeText);
      setCopiedRecipe(true);
      setTimeout(() => setCopiedRecipe(false), 3000);
    } catch (err) {
      console.error('Failed to copy recipe:', err);
    }
  };

  const closeViewModal = () => {
    setViewingRecipe(null);
    setViewingServings(1);
    setCheckedInstructionIds(new Set());
  };

  const toggleInstructionChecked = (instructionId: string) => {
    setCheckedInstructionIds((current) => {
      const next = new Set(current);

      if (next.has(instructionId)) {
        next.delete(instructionId);
      } else {
        next.add(instructionId);
      }

      return next;
    });
  };
  
  // Handle inventory icon click - navigate to inventory with search
  const handleInventoryClick = (ingredientName: string) => {
    navigate(`/app/inventory?search=${encodeURIComponent(ingredientName)}`);
  };
  
  // Handle shopping list icon click
  const handleShoppingListClick = async (ingredientName: string) => {
    const favorite = favorites.find(f => f.name === ingredientName);
    const primaryList = shoppingLists.find(l => l.isPrimary);
    
    if (favorite && primaryList) {
      // Quick add from favorite
      try {
        await createShoppingItem({
          listId: primaryList.id,
          name: favorite.name,
          category: favorite.category,
          notes: favorite.notes,
          link: favorite.link,
          store: favorite.store,
          aisle: favorite.aisle,
          quantity: 1,
          purchased: false,
        });
        
        // Show success notification
        setAddItemNotification(ingredientName);
        setTimeout(() => setAddItemNotification(''), 3000);
      } catch (error) {
        console.error('Failed to add to shopping list:', error);
      }
    } else {
      // Not a favorite or no primary list - open modal
      setShoppingItemForm({
        listId: primaryList?.id || '',
        name: ingredientName,
        category: 'mat',
        notes: '',
        quantity: 1,
        store: '',
        aisle: '',
      });
      setShowAddToShoppingModal(true);
    }
  };
  
  // Add ingredient
  const addIngredient = () => {
    const newIngredient: RecipeIngredient = {
      id: `ing_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      item: '',
      quantity: 1,
      unit: 'st',
      order: formIngredients.length,
    };
    setFormIngredients([...formIngredients, newIngredient]);
  };
  
  // Update ingredient
  const updateIngredient = (id: string, field: keyof RecipeIngredient, value: any) => {
    setFormIngredients(
      formIngredients.map((ing) => (ing.id === id ? { ...ing, [field]: value } : ing))
    );
  };
  
  // Remove ingredient
  const removeIngredient = (id: string) => {
    setFormIngredients(formIngredients.filter((ing) => ing.id !== id));
  };
  
  // Move ingredient up
  const moveIngredientUp = (id: string) => {
    const index = formIngredients.findIndex((ing) => ing.id === id);
    if (index > 0) {
      const newIngredients = [...formIngredients];
      [newIngredients[index], newIngredients[index - 1]] = [newIngredients[index - 1], newIngredients[index]];
      newIngredients.forEach((ing, idx) => ing.order = idx);
      setFormIngredients(newIngredients);
    }
  };
  
  // Move ingredient down
  const moveIngredientDown = (id: string) => {
    const index = formIngredients.findIndex((ing) => ing.id === id);
    if (index < formIngredients.length - 1) {
      const newIngredients = [...formIngredients];
      [newIngredients[index], newIngredients[index + 1]] = [newIngredients[index + 1], newIngredients[index]];
      newIngredients.forEach((ing, idx) => ing.order = idx);
      setFormIngredients(newIngredients);
    }
  };
  
  // Add instruction
  const addInstruction = () => {
    const newInstruction: RecipeInstruction = {
      id: `inst_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      step: formInstructions.length + 1,
      text: '',
    };
    setFormInstructions([...formInstructions, newInstruction]);
  };
  
  // Update instruction
  const updateInstruction = (id: string, text: string) => {
    setFormInstructions(
      formInstructions.map((inst) => (inst.id === id ? { ...inst, text } : inst))
    );
  };
  
  // Remove instruction
  const removeInstruction = (id: string) => {
    const newInstructions = formInstructions.filter((inst) => inst.id !== id);
    newInstructions.forEach((inst, idx) => inst.step = idx + 1);
    setFormInstructions(newInstructions);
  };
  
  // Move instruction up
  const moveInstructionUp = (id: string) => {
    const index = formInstructions.findIndex((inst) => inst.id === id);
    if (index > 0) {
      const newInstructions = [...formInstructions];
      [newInstructions[index], newInstructions[index - 1]] = [newInstructions[index - 1], newInstructions[index]];
      newInstructions.forEach((inst, idx) => inst.step = idx + 1);
      setFormInstructions(newInstructions);
    }
  };
  
  // Move instruction down
  const moveInstructionDown = (id: string) => {
    const index = formInstructions.findIndex((inst) => inst.id === id);
    if (index < formInstructions.length - 1) {
      const newInstructions = [...formInstructions];
      [newInstructions[index], newInstructions[index + 1]] = [newInstructions[index + 1], newInstructions[index]];
      newInstructions.forEach((inst, idx) => inst.step = idx + 1);
      setFormInstructions(newInstructions);
    }
  };
  
  // Add tag
  const addTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !formTags.includes(trimmedTag)) {
      setFormTags([...formTags, trimmedTag]);
      setTagInput('');
    }
  };
  
  // Remove tag
  const removeTag = (tag: string) => {
    setFormTags(formTags.filter((t) => t !== tag));
  };
  
  // Save recipe
  const handleSave = async () => {
    if (!formDish.trim()) {
      setErrorMessage('Vänligen ange rätt');
      return;
    }
    
    if (formIngredients.length === 0) {
      setErrorMessage('Vänligen lägg till minst en ingrediens');
      return;
    }
    
    if (formInstructions.length === 0) {
      setErrorMessage('Vänligen lägg till minst en instruktion');
      return;
    }
    
    const recipeData = {
      dish: formDish.trim().slice(0, 50),
      description: formDescription.trim().slice(0, 500),
      servings: formServings,
      ingredients: formIngredients,
      instructions: formInstructions,
      tags: formTags,
      isPrivate: false,
      isPinned: false,
    };
    
    try {
      if (editingRecipe) {
        const editedRecipeId = editingRecipe.id;
        await updateRecipe(editingRecipe.id, recipeData);
        const updatedRecipe = useRecipesStore.getState().getRecipeById(editedRecipeId);

        setEditingRecipe(null);
        const shouldRestoreView = shouldReturnToViewAfterEdit;
        setShouldReturnToViewAfterEdit(false);
        resetForm();

        if (shouldRestoreView) {
          openViewModal(updatedRecipe ?? {
            ...editingRecipe,
            ...recipeData,
          });
        }
      } else {
        await addRecipe(recipeData);
        closeAddModal();
      }
    } catch (error) {
      console.error('Failed to save recipe:', error);
      setErrorMessage('Kunde inte spara receptet. Försök igen.');
    }
  };
  
  // Delete recipe
  const handleDelete = async () => {
    if (!confirmDeleteRecipe) return;
    
    try {
      await deleteRecipe(confirmDeleteRecipe.id);
      setConfirmDeleteRecipe(null);
    } catch (error) {
      console.error('Failed to delete recipe:', error);
      setErrorMessage('Kunde inte radera receptet. Försök igen.');
    }
  };
  
  // Calculate scaled ingredient quantity
  const scaleIngredient = (ingredient: RecipeIngredient, baseServings: number, targetServings: number): string => {
    const scaledQuantity = (ingredient.quantity / baseServings) * targetServings;
    // Format to 2 decimals if needed
    return scaledQuantity % 1 === 0 ? scaledQuantity.toString() : scaledQuantity.toFixed(2);
  };
  
  if (!isAuthenticated) {
    return (
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="rounded-xl border-2 border-dashed border-border p-12 text-center">
          <ChefHat className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Logga in för att se dina recept.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold flex items-center">
          <ChefHat className="h-6 w-6 text-primary mr-3 shrink-0" />
          Receptbok
        </h1>
        <p className="text-muted-foreground">Samla dina beredskapsrecept och planera måltider i både vardag och kris.</p>
        {!hasPremiumAccess && totalCount > 0 && (
          <p className="text-sm text-amber-400">
            Du har {totalCount} recept. Uppgradera till Premium för att använda Receptboken.
          </p>
        )}
      </div>

      {isRefreshingRecipes && loadedScopeKey === recipeScopeKey && (
        <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
          <div className="h-2.5 w-2.5 rounded-full bg-[#6d7c48] animate-pulse" />
          <span>Uppdaterar receptlistan i bakgrunden...</span>
        </div>
      )}

      {recipeLoadError && (
        <div className="flex flex-col gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-4 text-sm md:flex-row md:items-center md:justify-between">
          <p className="text-amber-100">{recipeLoadError}</p>
          <button
            onClick={retryRecipeLoad}
            className="rounded-lg border border-amber-400/40 px-3 py-2 font-medium text-amber-100 transition-colors hover:bg-amber-400/10"
          >
            Försök igen
          </button>
        </div>
      )}

      {/* Navigation links to related pages */}
      <div className="space-y-2">
        <Link
          to="/app/inventory"
          className="flex items-center justify-between w-full p-4 rounded-xl bg-orange-400/10 border border-orange-400/30 hover:bg-orange-400/20 transition-all"
        >
          <div className="flex items-center gap-3">
            <ChevronLeft className="h-6 w-6 text-orange-400" />
            <p className="font-semibold text-orange-400">Beredskapsförråd</p>
          </div>
          <Package className="h-5 w-5 text-orange-400" />
        </Link>
        
        <Link
          to="/app/shopping-list"
          className="w-full rounded-xl p-4 transition-colors flex items-center justify-between group border"
          style={{ backgroundColor: 'rgba(167,139,250,0.15)', borderColor: 'rgba(167,139,250,0.15)' }}
        >
          <div className="flex items-center gap-3">
            <ShoppingCart className="h-5 w-5 text-purple-300" />
            <p className="font-semibold text-purple-300">Inköpslista</p>
          </div>
          <ChevronRight className="h-5 w-5 text-purple-300 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>

      {/* Search and controls */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Sök recept..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 rounded-lg border border-border bg-background text-foreground"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Grid/List toggle */}
        <div className="hidden md:flex items-center gap-1 bg-muted rounded-lg p-1">
          <button
            onClick={() => setGridColumns('gallery')}
            className={`p-2 rounded transition-colors ${
              gridColumns === 'gallery'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            title="Galleri (3 kolumner)"
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            onClick={() => setGridColumns('single')}
            className={`p-2 rounded transition-colors ${
              gridColumns === 'single'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            title="Enkel kolumn"
          >
            <Rows3 className="h-4 w-4" />
          </button>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => setShowMealPlanModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium transition-colors"
            style={{ backgroundColor: '#9b6d47' }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#7d5936')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#9b6d47')}
            title="Matsedel"
          >
            <UtensilsCrossed className="h-4 w-4" />
          </button>
          
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium transition-colors"
            style={{ backgroundColor: '#6d7c48' }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#5a6739')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#6d7c48')}
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Tag filter */}
      {allTags.length > 0 && (
        <div className="flex gap-2 flex-wrap overflow-x-auto">
          <button
            onClick={() => setSelectedTag(null)}
            className="px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-all"
            style={
              !selectedTag
                ? {
                    backgroundColor: 'rgba(109, 124, 72, 0.25)',
                    color: '#a8b88d',
                    border: '2px solid #a8b88d',
                    boxShadow: '0 0 15px rgba(109, 124, 72, 0.4), 0 0 25px rgba(109, 124, 72, 0.2)',
                  }
                : {
                    backgroundColor: 'rgba(109, 124, 72, 0.08)',
                    color: '#a8b88d',
                    border: '1px solid rgba(109, 124, 72, 0.3)',
                  }
            }
            onMouseEnter={(e) => {
              if (selectedTag) {
                e.currentTarget.style.backgroundColor = 'rgba(109, 124, 72, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(109, 124, 72, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedTag) {
                e.currentTarget.style.backgroundColor = 'rgba(109, 124, 72, 0.08)';
                e.currentTarget.style.borderColor = 'rgba(109, 124, 72, 0.3)';
              }
            }}
          >
            Alla
          </button>
          {allTags.map((tag) => (
            <button
              key={tag}
              onClick={() => setSelectedTag(tag === selectedTag ? null : tag)}
              className="px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-all"
              style={
                selectedTag === tag
                  ? {
                      backgroundColor: 'rgba(109, 124, 72, 0.25)',
                      color: '#a8b88d',
                      border: '2px solid #a8b88d',
                      boxShadow: '0 0 15px rgba(109, 124, 72, 0.4), 0 0 25px rgba(109, 124, 72, 0.2)',
                    }
                  : {
                      backgroundColor: 'rgba(109, 124, 72, 0.08)',
                      color: '#a8b88d',
                      border: '1px solid rgba(109, 124, 72, 0.3)',
                    }
              }
              onMouseEnter={(e) => {
                if (selectedTag !== tag) {
                  e.currentTarget.style.backgroundColor = 'rgba(109, 124, 72, 0.15)';
                  e.currentTarget.style.borderColor = 'rgba(109, 124, 72, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedTag !== tag) {
                  e.currentTarget.style.backgroundColor = 'rgba(109, 124, 72, 0.08)';
                  e.currentTarget.style.borderColor = 'rgba(109, 124, 72, 0.3)';
                }
              }}
            >
              {tag}
            </button>
          ))}
        </div>
      )}
      
      {/* Recipes List */}
      <div>
        {filteredRecipes.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-border p-12 text-center">
            {isLoadingRecipes ? (
              <>
                <div className="h-12 w-12 mx-auto mb-4 rounded-full bg-muted/30 animate-pulse" />
                <p className="text-muted-foreground">Laddar recept...</p>
              </>
            ) : (
              <>
                <ChefHat className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {searchQuery || selectedTag
                    ? 'Inga recept matchar din sökning'
                    : 'Inga recept ännu.'}
                </p>
              </>
            )}
          </div>
        ) : (
          <div className={`grid gap-4 ${gridColumns === 'gallery' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
            {filteredRecipes.map((recipe) => (
              <div
                key={recipe.id}
                className="rounded-lg border border-border bg-card p-4 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => {
                  if (!hasPremiumAccess) {
                    setPremiumFeature('Receptbok');
                    setShowPremiumModal(true);
                    return;
                  }
                  openViewModal(recipe);
                }}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground line-clamp-2">{recipe.dish}</h3>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!hasPremiumAccess) {
                            setPremiumFeature('Receptbok');
                            setShowPremiumModal(true);
                            return;
                          }
                          addToMealPlan(recipe.id, recipe.dish);
                          setAddMealPlanNotification(recipe.dish);
                          setTimeout(() => setAddMealPlanNotification(''), 3000);
                        }}
                        className="p-1.5 rounded hover:bg-muted transition-colors"
                        title="Lägg till i matsedel"
                      >
                        <UtensilsCrossed
                          className="h-4 w-4"
                          style={{ color: recipesInMealPlan.has(recipe.id) ? '#9ca3af' : '#9b6d47' }}
                        />
                      </button>
                      
                      {/* Success notification */}
                      {addMealPlanNotification === recipe.dish && (
                        <div className="absolute right-0 top-full mt-1 z-50 flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border shadow-lg animate-in fade-in slide-in-from-right-1 whitespace-nowrap">
                          <Check className="h-4 w-4" style={{ color: '#9b6d47' }} />
                          <span className="text-sm font-medium">Tillagt i matsedel</span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePin(recipe.id);
                      }}
                      className="p-1.5 rounded hover:bg-muted transition-colors"
                      title={recipe.isPinned ? 'Ta bort pin' : 'Fäst'}
                    >
                      <Pin className={`h-4 w-4 ${recipe.isPinned ? 'fill-current' : ''}`} style={{ color: recipe.isPinned ? '#6d7c48' : undefined }} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(recipe);
                      }}
                      className="p-1.5 rounded hover:bg-muted transition-colors"
                      title="Redigera"
                    >
                      <Edit2 className="h-4 w-4 text-muted-foreground" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!hasPremiumAccess) {
                          setPremiumFeature('Receptbok');
                          setShowPremiumModal(true);
                          return;
                        }
                        setConfirmDeleteRecipe(recipe);
                      }}
                      className="p-1.5 rounded hover:bg-muted transition-colors"
                      title="Radera"
                    >
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </button>
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                  {recipe.description || 'Ingen beskrivning'}
                </p>
                
                <div className="flex items-center gap-4 mb-3 text-sm text-muted-foreground">
                  <span>{recipe.servings} portioner</span>
                  <span>{recipe.ingredients.length} ingredienser</span>
                </div>
                
                {recipe.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-1">
                    {recipe.tags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTag((current) => (current === tag ? null : tag));
                        }}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border border-transparent transition-colors"
                        style={
                          selectedTag === tag
                            ? {
                                backgroundColor: 'rgba(109, 124, 72, 0.2)',
                                color: '#c5d3a7',
                                borderColor: 'rgba(109, 124, 72, 0.8)',
                              }
                            : {
                                backgroundColor: 'rgba(109, 124, 72, 0.15)',
                                color: '#a8b88d',
                                borderColor: 'rgba(109, 124, 72, 0.4)',
                              }
                        }
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Recipe Modal */}
      {(showAddModal || editingRecipe) && (
        <ModalBackdrop className="items-start overflow-y-auto pt-28">
          <div 
            className="w-full max-w-4xl rounded-xl bg-card border border-border p-6 my-8 max-h-[90vh] overflow-y-auto" 
            onClick={(e) => e.stopPropagation()}
            style={{ overscrollBehavior: 'contain', touchAction: 'pan-y' }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">
                {editingRecipe ? 'Redigera recept' : 'Nytt recept'}
              </h2>
              <button
                onClick={editingRecipe ? closeEditModal : closeAddModal}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Dish name */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Rätt <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formDish}
                  onChange={(e) => setFormDish(e.target.value)}
                  maxLength={50}
                  placeholder="T.ex. Pasta carbonara"
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {formDish.length} / 50 tecken
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-2">Beskrivning</label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder="Kort beskrivning av receptet..."
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground resize-none"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {formDescription.length} / 500 tecken
                </p>
              </div>

              {/* Servings */}
              <div>
                <label className="block text-sm font-medium mb-2">Antal portioner</label>
                <input
                  type="number"
                  min={1}
                  max={1000}
                  value={formServings}
                  onChange={(e) => setFormServings(parseInt(e.target.value) || 1)}
                  className="w-32 px-4 py-2 rounded-lg border border-border bg-background text-foreground"
                />
              </div>

              {/* Ingredients */}
              <div>
                <label className="block text-sm font-medium mb-3">
                  Ingredienser <span className="text-red-400">*</span>
                </label>
                <div className="space-y-2">
                  {formIngredients.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">Inga ingredienser tillagda ännu</p>
                  ) : (
                    formIngredients.map((ingredient, index) => (
                      <div key={ingredient.id} className="flex flex-wrap items-start gap-2 p-3 rounded-lg border border-border bg-muted/30">
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => moveIngredientUp(ingredient.id)}
                            disabled={index === 0}
                            className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Flytta upp"
                          >
                            <ChevronUp className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => moveIngredientDown(ingredient.id)}
                            disabled={index === formIngredients.length - 1}
                            className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Flytta ner"
                          >
                            <ChevronDown className="h-3 w-3" />
                          </button>
                        </div>
                        <input
                          type="text"
                          value={ingredient.item}
                          onChange={(e) => updateIngredient(ingredient.id, 'item', e.target.value)}
                          placeholder="Vara"
                          className="flex-1 min-w-[200px] px-3 py-2 rounded border border-border bg-background"
                        />
                        <div className="flex gap-1 items-center">
                          <button
                            type="button"
                            onClick={() => {
                              const currentQty = ingredient.quantity || 0;
                              const newQty = Math.max(0, currentQty - 0.5);
                              updateIngredient(ingredient.id, 'quantity', newQty);
                            }}
                            className="p-1.5 rounded border border-border hover:bg-muted"
                            title="Minska med 0,5"
                          >
                            <MinusCircle className="h-4 w-4" />
                          </button>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={ingredient.quantity || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Allow empty string or valid numbers
                              if (value === '' || value === '-') {
                                updateIngredient(ingredient.id, 'quantity', 0);
                              } else {
                                const parsed = parseFloat(value);
                                if (!isNaN(parsed)) {
                                  updateIngredient(ingredient.id, 'quantity', parsed);
                                }
                              }
                            }}
                            placeholder="0"
                            className="w-20 px-3 py-2 rounded border border-border bg-background text-center"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const currentQty = ingredient.quantity || 0;
                              const newQty = currentQty + 0.5;
                              updateIngredient(ingredient.id, 'quantity', newQty);
                            }}
                            className="p-1.5 rounded border border-border hover:bg-muted"
                            title="Öka med 0,5"
                          >
                            <PlusCircle className="h-4 w-4" />
                          </button>
                          <select
                            value={ingredient.unit}
                            onChange={(e) => updateIngredient(ingredient.id, 'unit', e.target.value)}
                            className="w-24 px-3 py-2 rounded border border-border bg-background text-foreground"
                          >
                            {COMMON_UNITS.map((unit) => (
                              <option key={unit} value={unit}>
                                {unit}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          onClick={() => removeIngredient(ingredient.id)}
                          className="p-2 rounded hover:bg-muted text-red-400"
                          title="Ta bort"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
                <button
                  onClick={addIngredient}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-white mt-3"
                  style={{ backgroundColor: '#6d7c48' }}
                  onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#5a6739')}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#6d7c48')}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>

              {/* Instructions */}
              <div>
                <label className="block text-sm font-medium mb-3">
                  Instruktioner <span className="text-red-400">*</span>
                </label>
                <div className="space-y-2">
                  {formInstructions.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">Inga instruktioner tillagda ännu</p>
                  ) : (
                    formInstructions.map((instruction, index) => (
                      <div key={instruction.id} className="flex items-start gap-2 p-3 rounded-lg border border-border bg-muted/30">
                        <div className="flex flex-col gap-1 pt-1">
                          <button
                            onClick={() => moveInstructionUp(instruction.id)}
                            disabled={index === 0}
                            className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Flytta upp"
                          >
                            <ChevronUp className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => moveInstructionDown(instruction.id)}
                            disabled={index === formInstructions.length - 1}
                            className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Flytta ner"
                          >
                            <ChevronDown className="h-3 w-3" />
                          </button>
                        </div>
                        <span className="font-semibold text-lg pt-1" style={{ color: '#6d7c48' }}>
                          {instruction.step}.
                        </span>
                        <textarea
                          value={instruction.text}
                          onChange={(e) => updateInstruction(instruction.id, e.target.value)}
                          placeholder="Beskriv steget..."
                          rows={2}
                          className="flex-1 px-3 py-2 rounded border border-border bg-background resize-none"
                        />
                        <button
                          onClick={() => removeInstruction(instruction.id)}
                          className="p-2 rounded hover:bg-muted text-red-400"
                          title="Ta bort"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
                <button
                  onClick={addInstruction}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-white mt-3"
                  style={{ backgroundColor: '#6d7c48' }}
                  onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#5a6739')}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#6d7c48')}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium mb-2">Taggar</label>

                {allTags.length > 0 && (
                  <div className="relative mb-2">
                    <button
                      type="button"
                      onClick={() => setShowTagPicker((prev) => !prev)}
                      className="w-full px-3 py-2 border border-border bg-input text-foreground text-sm rounded-lg flex items-center justify-between gap-2 hover:bg-muted transition-colors"
                    >
                      <span className="truncate text-left">
                        Välj befintlig tagg...
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 flex-shrink-0 transition-transform ${showTagPicker ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {showTagPicker && (
                      <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-card shadow-lg max-h-60 overflow-y-auto">
                        {allTags
                          .filter((tag) => !formTags.includes(tag))
                          .map((tag) => (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => {
                                if (!formTags.includes(tag)) {
                                  setFormTags([...formTags, tag]);
                                }
                                setShowTagPicker(false);
                              }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors"
                            >
                              {tag}
                            </button>
                          ))}
                        {allTags.filter((tag) => !formTags.includes(tag)).length === 0 && (
                          <div className="px-3 py-2 text-xs text-muted-foreground italic">
                            Inga fler taggar att välja
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                    placeholder="Lägg till tagg..."
                    className="flex-1 px-4 py-2 rounded-lg border border-border bg-background text-foreground"
                  />
                  <button
                    onClick={addTag}
                    className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                  >
                    Lägg till
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formTags.map((tag) => (
                    <span
                      key={tag}
                      className="flex items-center gap-1 px-3 py-1 rounded-lg text-sm"
                      style={{
                        backgroundColor: 'rgba(109, 124, 72, 0.15)',
                        color: '#a8b88d',
                      }}
                    >
                      {tag}
                      <button
                        onClick={() => removeTag(tag)}
                        className="hover:text-foreground transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer buttons */}
            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-border">
              <button
                onClick={editingRecipe ? closeEditModal : closeAddModal}
                className="px-6 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-2 rounded-lg text-white font-medium transition-colors"
                style={{ backgroundColor: '#6d7c48' }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#5a6739')}
                onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#6d7c48')}
              >
                {editingRecipe ? 'Spara ändringar' : 'Skapa recept'}
              </button>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* View Recipe Modal */}
      {viewingRecipe && (
        <ModalBackdrop className="items-start overflow-y-auto pt-28">
          <div 
            className="w-full max-w-6xl rounded-xl bg-card border border-border p-6 my-8 max-h-[90vh] overflow-y-auto" 
            onClick={(e) => e.stopPropagation()}
            style={{ overscrollBehavior: 'contain', touchAction: 'pan-y' }}
          >
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-6">
              <div className="flex gap-2 flex-shrink-0 self-end md:self-auto order-1 md:order-2">
                <div className="relative">
                  <button
                    onClick={() => {
                      if (!hasPremiumAccess) {
                        setPremiumFeature('Receptbok');
                        setShowPremiumModal(true);
                        return;
                      }
                      addToMealPlan(viewingRecipe.id, viewingRecipe.dish);
                      setAddMealPlanNotification(viewingRecipe.dish);
                      setTimeout(() => setAddMealPlanNotification(''), 3000);
                    }}
                    className="p-2 rounded-lg hover:bg-muted transition-colors"
                    title="Lägg till i matsedel"
                  >
                    <UtensilsCrossed
                      className="h-5 w-5"
                      style={{ color: recipesInMealPlan.has(viewingRecipe.id) ? '#9ca3af' : '#9b6d47' }}
                    />
                  </button>
                  
                  {/* Success notification */}
                  {addMealPlanNotification === viewingRecipe.dish && (
                    <div className="absolute right-0 top-full mt-1 z-50 flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border shadow-lg animate-in fade-in slide-in-from-right-1 whitespace-nowrap">
                      <Check className="h-4 w-4" style={{ color: '#9b6d47' }} />
                      <span className="text-sm font-medium">Tillagt i matsedel</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    const recipe = viewingRecipe;
                    closeViewModal();
                    openEditModal(recipe, { returnToViewOnClose: true });
                  }}
                  className="p-2 rounded-lg hover:bg-muted transition-colors"
                  title="Redigera recept"
                >
                  <Edit2 className="h-5 w-5" />
                </button>
                <div className="relative">
                  <button
                    onClick={copyRecipeToClipboard}
                    className="p-2 rounded-lg hover:bg-muted transition-colors"
                    title="Kopiera recept"
                  >
                    <Copy className="h-5 w-5" style={{ color: copiedRecipe ? '#10b981' : 'currentColor' }} />
                  </button>
                  
                  {/* Success notification */}
                  {copiedRecipe && (
                    <div className="absolute right-0 top-full mt-1 z-50 flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border shadow-lg animate-in fade-in slide-in-from-right-1 whitespace-nowrap">
                      <Check className="h-4 w-4 text-green-500" />
                      <span className="text-sm font-medium">Kopierat till urklipp</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={closeViewModal}
                  className="p-2 rounded-lg hover:bg-muted transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex-1 min-w-0 order-2 md:order-1">
                <h2 className="text-2xl font-bold mb-3">{viewingRecipe.dish}</h2>
                {viewingRecipe.description && (
                  <p className="text-muted-foreground">{viewingRecipe.description}</p>
                )}
              </div>
            </div>

            {/* Tags */}
            {viewingRecipe.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {viewingRecipe.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-lg text-sm"
                    style={{
                      backgroundColor: 'rgba(109, 124, 72, 0.15)',
                      color: '#a8b88d',
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Servings adjustment */}
            <div className="flex items-center gap-3 mb-6 p-4 rounded-lg border border-border bg-muted/30">
              <span className="text-sm font-medium">Portioner:</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewingServings(Math.max(1, viewingServings - 1))}
                  className="p-1 rounded hover:bg-muted"
                >
                  <MinusCircle className="h-5 w-5" style={{ color: '#6d7c48' }} />
                </button>
                <span className="font-semibold text-lg w-12 text-center">{viewingServings}</span>
                <button
                  onClick={() => setViewingServings(viewingServings + 1)}
                  className="p-1 rounded hover:bg-muted"
                >
                  <PlusCircle className="h-5 w-5" style={{ color: '#6d7c48' }} />
                </button>
              </div>
            </div>

            {/* Ingredients and Instructions - Two columns on desktop */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Ingredients */}
              <div>
                <h3 className="text-lg font-semibold mb-3" style={{ color: '#6d7c48' }}>
                  Ingredienser
                </h3>
                <div className="space-y-2">
                  {viewingRecipe.ingredients
                    .sort((a, b) => a.order - b.order)
                    .map((ingredient) => {
                      // Check if ingredient exists in inventory (case-insensitive partial match, quantity >= 1)
                      const ingredientLower = ingredient.item.toLowerCase();
                      const inInventory = inventoryItems.some(
                        item => {
                          const itemNameLower = item.name.toLowerCase();
                          return (itemNameLower.includes(ingredientLower) || ingredientLower.includes(itemNameLower)) 
                            && item.quantity >= 1;
                        }
                      );
                      
                      // Check if ingredient exists as favorite (case-insensitive partial match)
                      const isFavorite = favorites.some(f => {
                        const favNameLower = f.name.toLowerCase();
                        return favNameLower.includes(ingredientLower) || ingredientLower.includes(favNameLower);
                      });
                      
                      return (
                        <div key={ingredient.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/20">
                          <span className="font-medium">
                            {scaleIngredient(ingredient, viewingRecipe.servings, viewingServings)} {ingredient.unit}
                          </span>
                          <span className="flex-1 text-muted-foreground">{ingredient.item}</span>
                          
                          {/* Icon buttons */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Inventory icon */}
                            <button
                              onClick={() => handleInventoryClick(ingredient.item)}
                              className="p-1.5 rounded hover:bg-muted/60 transition-colors"
                              title={inInventory ? 'Finns i förråd' : 'Sök i förråd'}
                            >
                              <Package 
                                className="h-5 w-5"
                                style={{ color: inInventory ? '#ff8c00' : '#94a3b8' }}
                              />
                            </button>
                            
                            {/* Shopping list icon with notification */}
                            <div className="relative">
                              <button
                                onClick={() => handleShoppingListClick(ingredient.item)}
                                className="p-1.5 rounded hover:bg-muted/60 transition-colors"
                                title="Lägg till i primär inköpslista"
                              >
                                <ShoppingCart 
                                  className="h-5 w-5"
                                  style={{ color: isFavorite ? '#c789ff' : '#94a3b8' }}
                                />
                              </button>
                              
                              {/* Success notification */}
                              {addItemNotification === ingredient.item && (
                                <div className="absolute right-0 top-full mt-1 z-50 flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border shadow-lg animate-in fade-in slide-in-from-right-1 whitespace-nowrap">
                                  <Check className="h-4 w-4" style={{ color: '#c789ff' }} />
                                  <span className="text-sm font-medium">Tillagt i inköpslistan</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* Instructions */}
              <div>
                <h3 className="text-lg font-semibold mb-3" style={{ color: '#6d7c48' }}>
                  Instruktioner
                </h3>
                <div className="space-y-3">
                  {viewingRecipe.instructions
                    .sort((a, b) => a.step - b.step)
                    .map((instruction) => {
                      const isChecked = checkedInstructionIds.has(instruction.id);

                      return (
                        <label
                          key={instruction.id}
                          className={`flex gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                            isChecked ? 'border-[#6d7c48] bg-[#6d7c48]/10' : 'border-border bg-muted/20'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleInstructionChecked(instruction.id)}
                            className="mt-1 h-5 w-5 flex-shrink-0 accent-[#6d7c48]"
                            aria-label={`Markera steg ${instruction.step} som klart`}
                          />
                          <span className="font-bold text-xl flex-shrink-0" style={{ color: '#6d7c48' }}>
                            {instruction.step}.
                          </span>
                          <p className={`pt-0.5 ${isChecked ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                            {instruction.text}
                          </p>
                        </label>
                      );
                    })}
                </div>
              </div>
            </div>

            {/* Footer info */}
            <div className="mt-6 pt-6 border-t border-border text-xs text-muted-foreground">
              <p>Skapad av {viewingRecipe.authorName}</p>
              {viewingRecipe.lastModifiedBy && (
                <p>Senast ändrad av {viewingRecipe.lastModifiedBy}</p>
              )}
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDeleteRecipe && (
        <ModalBackdrop className="items-start overflow-y-auto pt-28">
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-6 translate-y-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-semibold mb-4">Radera recept?</h2>
            <p className="text-muted-foreground mb-6">
              Är du säker på att du vill radera receptet "{confirmDeleteRecipe.dish}"? Detta kan inte ångras.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDeleteRecipe(null)}
                className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                Radera
              </button>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Error Modal */}
      {errorMessage && (
        <ModalBackdrop className="items-start overflow-y-auto pt-28">
          <div className="w-full max-w-md rounded-xl bg-card border border-border p-6 translate-y-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-semibold mb-4">Information</h2>
            <p className="text-muted-foreground mb-6">
              {errorMessage}
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setErrorMessage('')}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Add to Shopping List Modal */}
      {showAddToShoppingModal && (
        <ModalBackdrop className="items-start overflow-y-auto pt-28">
          <div 
            className="w-full max-w-md rounded-xl bg-card border border-border p-6 my-8 max-h-[90vh] overflow-y-auto" 
            onClick={(e) => e.stopPropagation()}
            style={{ overscrollBehavior: 'contain', touchAction: 'pan-y' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Lägg till i inköpslista</h3>
              <button onClick={() => setShowAddToShoppingModal(false)} className="p-1 rounded hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              {/* List Selection */}
              <div>
                <label className="block text-sm font-medium mb-1">Inköpslista</label>
                <select
                  value={shoppingItemForm.listId}
                  onChange={(e) => setShoppingItemForm(prev => ({ ...prev, listId: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                >
                  <option value="">Välj lista</option>
                  {shoppingLists.map(list => (
                    <option key={list.id} value={list.id}>
                      {list.name} {list.isPrimary ? '(Primär)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-1">Namn</label>
                <input
                  type="text"
                  value={shoppingItemForm.name}
                  maxLength={50}
                  onChange={(e) => setShoppingItemForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                />
                <div className="text-xs text-muted-foreground mt-1">{shoppingItemForm.name.length}/50</div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium mb-1">Kategori</label>
                <select
                  value={shoppingItemForm.category}
                  onChange={(e) => setShoppingItemForm(prev => ({ ...prev, category: e.target.value as InventoryCategory | '' }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                >
                  <option value="">Ingen kategori</option>
                  <option value="mat">{CATEGORY_LABELS.mat}</option>
                  <option value="medicin">{CATEGORY_LABELS.medicin}</option>
                  <option value="hygien">{CATEGORY_LABELS.hygien}</option>
                  <option value="verktyg">{CATEGORY_LABELS.verktyg}</option>
                  <option value="kommunikation">{CATEGORY_LABELS.kommunikation}</option>
                  <option value="varme">{CATEGORY_LABELS.varme}</option>
                  <option value="ljus">{CATEGORY_LABELS.ljus}</option>
                  <option value="dokument">{CATEGORY_LABELS.dokument}</option>
                  <option value="ovrigt">{CATEGORY_LABELS.ovrigt}</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium mb-1">Anteckningar</label>
                <textarea
                  value={shoppingItemForm.notes}
                  maxLength={200}
                  onChange={(e) => setShoppingItemForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                />
                <div className="text-xs text-muted-foreground mt-1">{shoppingItemForm.notes.length}/200</div>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium mb-1">Antal</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShoppingItemForm(prev => ({ ...prev, quantity: Math.max(1, prev.quantity - 1) }))}
                    disabled={shoppingItemForm.quantity <= 1}
                    className="p-2 rounded-lg border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={shoppingItemForm.quantity}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setShoppingItemForm(prev => ({ ...prev, quantity: Math.min(1000, Math.max(1, parseInt(val) || 1)) }));
                    }}
                    className="w-20 px-3 py-2 text-center rounded-lg border border-border bg-background"
                  />
                  <button
                    type="button"
                    onClick={() => setShoppingItemForm(prev => ({ ...prev, quantity: Math.min(1000, prev.quantity + 1) }))}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Store */}
              <div>
                <label className="block text-sm font-medium mb-1">Butik</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shoppingItemForm.store}
                    maxLength={50}
                    onChange={(e) => setShoppingItemForm(prev => ({ ...prev, store: e.target.value }))}
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background"
                    placeholder="T.ex. ICA Maxi"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setStoreSuggestionSearch('');
                      setShowStoreSuggestions(true);
                    }}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                    title="Visa befintliga butiker"
                  >
                    <Search className="h-5 w-5" />
                  </button>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{shoppingItemForm.store.length}/50</div>
              </div>

              {/* Aisle */}
              <div>
                <label className="block text-sm font-medium mb-1">Hylla/Avdelning</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shoppingItemForm.aisle}
                    maxLength={50}
                    onChange={(e) => setShoppingItemForm(prev => ({ ...prev, aisle: e.target.value }))}
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background"
                    placeholder="T.ex. Mejeri"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setAisleSuggestionSearch('');
                      setShowAisleSuggestions(true);
                    }}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
                    title="Visa avdelningar"
                  >
                    <Search className="h-5 w-5" />
                  </button>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{shoppingItemForm.aisle.length}/50</div>
              </div>

              {/* Buttons */}
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setShowAddToShoppingModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted"
                >
                  Avbryt
                </button>
                <button
                  onClick={async () => {
                    if (!shoppingItemForm.name.trim() || !shoppingItemForm.listId) return;
                    
                    try {
                      await createShoppingItem({
                        listId: shoppingItemForm.listId,
                        name: shoppingItemForm.name,
                        category: shoppingItemForm.category || undefined,
                        notes: shoppingItemForm.notes || undefined,
                        quantity: shoppingItemForm.quantity,
                        store: shoppingItemForm.store || undefined,
                        aisle: shoppingItemForm.aisle || undefined,
                        purchased: false,
                      });
                      
                      setShowAddToShoppingModal(false);
                      setAddItemNotification(shoppingItemForm.name);
                      setTimeout(() => setAddItemNotification(''), 3000);
                    } catch (error) {
                      console.error('Failed to add to shopping list:', error);
                    }
                  }}
                  className="flex-1 px-4 py-2 rounded-lg text-white hover:opacity-90"
                  style={{ backgroundColor: '#67724e' }}
                >
                  Spara
                </button>
              </div>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Store Suggestions Modal */}
      {showStoreSuggestions && (
        <ModalBackdrop className="items-start overflow-y-auto pt-28">
          <div 
            className="w-full max-w-md rounded-xl bg-card border border-border p-4 max-h-[80vh] flex flex-col my-8"
            style={{ overscrollBehavior: 'contain', touchAction: 'pan-y' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Välj butik</h3>
              <button onClick={() => setShowStoreSuggestions(false)} className="p-1 rounded hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={storeSuggestionSearch}
                  onChange={(e) => setStoreSuggestionSearch(e.target.value)}
                  placeholder="Sök butik..."
                  className="w-full pl-10 pr-3 py-2 rounded-lg border border-border bg-background"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {filteredStores.map((store) => (
                <button
                  key={store}
                  onClick={() => {
                    setShoppingItemForm(prev => ({ ...prev, store }));
                    setShowStoreSuggestions(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted transition-colors"
                >
                  {store}
                </button>
              ))}
              {filteredStores.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-8">
                  Inga butiker hittades
                </p>
              )}
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Aisle Suggestions Modal */}
      {showAisleSuggestions && (
        <ModalBackdrop className="items-start overflow-y-auto pt-28">
          <div 
            className="w-full max-w-md rounded-xl bg-card border border-border p-4 max-h-[80vh] flex flex-col my-8"
            style={{ overscrollBehavior: 'contain', touchAction: 'pan-y' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Välj avdelning</h3>
              <button onClick={() => setShowAisleSuggestions(false)} className="p-1 rounded hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={aisleSuggestionSearch}
                  onChange={(e) => setAisleSuggestionSearch(e.target.value)}
                  placeholder="Sök avdelning..."
                  className="w-full pl-10 pr-3 py-2 rounded-lg border border-border bg-background"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {filteredAisles.map((aisle) => (
                <button
                  key={aisle}
                  onClick={() => {
                    setShoppingItemForm(prev => ({ ...prev, aisle }));
                    setShowAisleSuggestions(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted transition-colors"
                >
                  {aisle}
                </button>
              ))}
              {filteredAisles.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-8">
                  Inga avdelningar hittades
                </p>
              )}
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Meal Plan Modal */}
      {showMealPlanModal && (
        <ModalBackdrop className="items-start overflow-y-auto pt-28">
          <div 
            className="w-full max-w-2xl rounded-xl bg-card border border-border p-6 my-8 max-h-[90vh] overflow-y-auto" 
            onClick={(e) => e.stopPropagation()}
            style={{ overscrollBehavior: 'contain', touchAction: 'pan-y' }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(155, 109, 71, 0.2)' }}>
                  <UtensilsCrossed className="h-6 w-6" style={{ color: '#9b6d47' }} />
                </div>
                <h2 className="text-2xl font-bold">Matsedel</h2>
              </div>
              <button onClick={() => setShowMealPlanModal(false)} className="p-2 rounded hover:bg-muted">
                <X className="h-5 w-5" />
              </button>
            </div>

            {mealPlanItems.length === 0 ? (
              <div className="text-center py-12">
                <UtensilsCrossed className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-2">Din matsedel är tom</p>
                <p className="text-sm text-muted-foreground">
                  Lägg till recept från listan genom att klicka på matsedel-ikonen
                </p>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleMealPlanDragEnd}
              >
                <SortableContext
                  items={mealPlanItems.map(item => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {mealPlanItems.map((item, index) => {
                      const isMarker = item.type === 'marker';
                      const recipeNumber = mealPlanItems
                        .slice(0, index)
                        .filter(i => i.type === 'recipe').length + (isMarker ? 0 : 1);
                      
                      return (
                        <SortableMealItem
                          key={item.id}
                          item={item}
                          recipeNumber={recipeNumber}
                          recipes={recipes}
                          onViewRecipe={(recipe) => {
                            openViewModal(recipe);
                            setShowMealPlanModal(false);
                          }}
                          onRemove={removeFromMealPlan}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            {/* Add marker button */}
            <div className="mt-6 pt-6 border-t border-border">
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => setShowAddMarkerModal(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-muted/50 transition-colors text-sm text-muted-foreground justify-center flex-1"
                >
                  <Flag className="h-4 w-4" />
                  Lägg till markör
                </button>
                <button
                  onClick={async () => {
                    const weekdays = ['Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag', 'Söndag'];
                    for (const day of weekdays) {
                      await addMealPlanMarker(day, undefined);
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-muted/50 transition-colors text-sm text-muted-foreground justify-center flex-1"
                >
                  <Flag className="h-4 w-4" />
                  Lägg till veckodagar
                </button>
                <button
                  onClick={async () => {
                    // Get Sunday of a specific ISO week
                    const getSundayOfWeek = (year: number, week: number): string => {
                      // January 4th is always in week 1
                      const jan4 = new Date(year, 0, 4);
                      const jan4Day = jan4.getDay() || 7; // Make Sunday = 7
                      
                      // Find Monday of week 1
                      const week1Monday = new Date(year, 0, 4 - jan4Day + 1);
                      
                      // Calculate Monday of target week
                      const targetMonday = new Date(week1Monday);
                      targetMonday.setDate(week1Monday.getDate() + (week - 1) * 7);
                      
                      // Calculate Sunday of target week (Monday + 6 days)
                      const targetSunday = new Date(targetMonday);
                      targetSunday.setDate(targetMonday.getDate() + 6);
                      
                      // Format as YYYY-MM-DD
                      const yyyy = targetSunday.getFullYear();
                      const mm = String(targetSunday.getMonth() + 1).padStart(2, '0');
                      const dd = String(targetSunday.getDate()).padStart(2, '0');
                      return `${yyyy}-${mm}-${dd}`;
                    };
                    
                    // Calculate current ISO week number
                    const getWeekNumber = (date: Date): number => {
                      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
                      const dayNum = d.getUTCDay() || 7;
                      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
                      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
                      return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
                    };
                    
                    const today = new Date();
                    const currentWeek = getWeekNumber(today);
                    const currentYear = today.getFullYear();
                    
                    for (let i = 0; i < 6; i++) {
                      let weekNum = currentWeek + i;
                      let year = currentYear;
                      
                      // Handle year transition
                      if (weekNum > 52) {
                        weekNum = weekNum - 52;
                        year = currentYear + 1;
                      }
                      
                      const sundayDate = getSundayOfWeek(year, weekNum);
                      await addMealPlanMarker(`Vecka ${weekNum}`, sundayDate);
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-muted/50 transition-colors text-sm text-muted-foreground justify-center flex-1"
                >
                  <Flag className="h-4 w-4" />
                  Lägg till veckonummer
                </button>
              </div>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {/* Add Marker Modal */}
      {showAddMarkerModal && (
        <ModalBackdrop className="items-start overflow-y-auto pt-28">
          <div 
            className="w-full max-w-md rounded-xl bg-card border border-border p-6 translate-y-6" 
            onClick={(e) => e.stopPropagation()}
            style={{ overscrollBehavior: 'contain', touchAction: 'pan-y' }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted">
                  <Flag className="h-5 w-5" />
                </div>
                <h2 className="text-xl font-bold">Lägg till markör</h2>
              </div>
              <button 
                onClick={() => {
                  setShowAddMarkerModal(false);
                  setMarkerForm({ label: '', endDate: '' });
                }} 
                className="p-2 rounded hover:bg-muted"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-muted-foreground mb-6">
              Använd till veckodagar, veckonummer, leveransdatum, eller annan typ av markering i matsedeln.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Etikett <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={markerForm.label}
                  onChange={(e) => setMarkerForm(prev => ({ ...prev, label: e.target.value }))}
                  placeholder="t.ex. Vecka 15, Måndag, Leverans"
                  maxLength={50}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {markerForm.label.length}/50 tecken
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Slutdatum (valfritt)
                </label>
                <input
                  type="date"
                  value={markerForm.endDate}
                  onChange={(e) => setMarkerForm(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddMarkerModal(false);
                  setMarkerForm({ label: '', endDate: '' });
                }}
                className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={async () => {
                  if (!markerForm.label.trim()) return;
                  
                  try {
                    await addMealPlanMarker(
                      markerForm.label.trim(),
                      markerForm.endDate || undefined
                    );
                    setShowAddMarkerModal(false);
                    setMarkerForm({ label: '', endDate: '' });
                  } catch (error) {
                    console.error('Failed to add marker:', error);
                  }
                }}
                disabled={!markerForm.label.trim()}
                className="px-4 py-2 rounded-lg text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#6d7c48' }}
                onMouseEnter={(e) => {
                  if (markerForm.label.trim()) {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#5a6739';
                  }
                }}
                onMouseLeave={(e) => {
                  if (markerForm.label.trim()) {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#6d7c48';
                  }
                }}
              >
                Lägg till
              </button>
            </div>
          </div>
        </ModalBackdrop>
      )}

      <PremiumRequiredModal
        open={showPremiumModal}
        feature={premiumFeature}
        onClose={() => setShowPremiumModal(false)}
      />
    </div>
  );
}
